import { Button, Col, Modal, Row } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { PackagesEnum } from '../../../../../../enum/PackageEnum';
import { useTranslation } from 'react-i18next';
import { BookingService } from '../../service';
import { ErrorToast, SuccessToast } from '../../../../../../utils/toasters';
import {
    getNumberContentPieces,
    getStatusIcon,
    getSubtotalChildOfferItems,
    getSubtotalOfferItems,
} from '../../../../../../shared/functions/Functions';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../store/rootReducer';
import { setOfferModalDetails } from '../../../../../../store/brand/childOfferBrandSlice';
import { priceSplitter } from '../../../../../../utils/helpers/priceSplitter';
import { ConfirmSendingOfferModal } from '../../../../../influencer/offer/modal/offer-modal/components/ConfirmSendingOfferModal';
import { gtmService } from '../../../../../../utils/gtm.service';
import HtmlToPdf from '../../../../../../shared/components/generate-pdf';
import { ChildOfferModalLeftSide } from './components/ChildOfferModalLeftSide';
import ChildOfferModalRightSide from './components/ChildOfferModalRightSide';
import { ConfirmationModal } from '../../../../../../shared/components/modal/ConfirmationModal';
import { OfferPdfDownloadButton } from '../../../../../../shared/components/pdf/DownloadOfferPdf';

interface IChildOfferModalProps {
    show: boolean;
    closeModal: () => void;
    setUpdateList?: React.Dispatch<React.SetStateAction<boolean>>;
    passedCampaignId?: number;
}

function ChildOfferModal({ show, closeModal, passedCampaignId, setUpdateList }: IChildOfferModalProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const {
        campaign,
        initialChildOffer,
        isCharityOrPrivate,
        isTransparentPricing,
        selectedUser,
        isEditable,
        canBeDeleted,
    } = useSelector((state: IAllStates) => state.childOfferBrand);
    const { company, isClient } = useSelector((state: IAllStates) => state.auth.user);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [disableButton, setDisableButton] = useState(false);

    const campaignId = +location?.pathname?.split('/')?.filter(currLoc => +currLoc)[0];
    const grandTotalSum = !isEditable
        ? getSubtotalChildOfferItems(initialChildOffer?.offerItems)
        : getSubtotalOfferItems(initialChildOffer?.offerItems);
    const grandTotalFeeSum = Math.ceil(grandTotalSum / (campaign?.feePercentage ?? 1));
    const grandTotalForDisplaying = isTransparentPricing
        ? priceSplitter(grandTotalFeeSum)
        : priceSplitter(grandTotalSum);

    useEffect(() => {
        if (show && selectedUser?.id) {
            BookingService.getChildOffer(selectedUser?.id, passedCampaignId ?? campaignId)
                .then(response => {
                    dispatch(setOfferModalDetails(response));
                })
                .catch(err => {
                    closeModal();
                    ErrorToast(err);
                });
        }
    }, [show, selectedUser]);

    const submitOfferRequest = () => {
        setDisableButton(true);
        let initialOfferCopy = initialChildOffer;
        const offerItems = isCharityOrPrivate
            ? initialOfferCopy.offerItems
            : initialOfferCopy.offerItems.filter(item => item.nbPosts > 0 && item.price > 0);
        const price = getSubtotalOfferItems(initialOfferCopy.offerItems);
        initialOfferCopy = { ...initialOfferCopy, offerItems, price };
        BookingService.submitChildOffer(initialOfferCopy, selectedUser?.id, campaignId)
            .then(() => {
                gtmService('brand_offer_submitted', grandTotalForDisplaying);
                setDisableButton(false);
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                }
                closeModal();
                SuccessToast(t('general.toasts.success_created_spendig_posting'));
            })
            .catch(error => {
                ErrorToast(error);
                setDisableButton(false);
            });
    };

    const deleteOfferAction = () => {
        BookingService.deleteChildOffer(selectedUser?.id, campaignId)
            .then(() => {
                closeModal();
                if (setUpdateList) {
                    setUpdateList(prevState => !prevState);
                    SuccessToast(t('general.toasts.success_deleted_spendig_posting'));
                }
            })
            .catch(error => {
                ErrorToast(error);
            });
    };

    const closeSubmitModal = () => {
        setShowSubmitModal(false);
    };

    const submitForm = (event: any) => {
        event.preventDefault();
        setShowSubmitModal(true);
    };

    return (
        <div onClick={e => e.stopPropagation()}>
            <Modal
                show={show}
                onHide={closeModal}
                id="offer-modal"
                size="xl"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <form onSubmit={event => submitForm(event)}>
                    <Modal.Body>
                        {campaign?.offers[0]?.id && (
                            <div className={`offer-status-badge ${campaign?.offers[0]?.status}`}>
                                {getStatusIcon(campaign?.offers[0]?.status ?? '')}
                                <p>{t(`general.offerStatus.${campaign?.offers[0]?.status}`)}</p>
                            </div>
                        )}
                        <Row className="h-100">
                            <Col xl={3} lg={12} className="profile-info pt-2">
                                <ChildOfferModalLeftSide campaignId={passedCampaignId ?? campaignId} />
                            </Col>
                            <Col xl={9} lg={12} className="d-flex" style={{ height: 'calc(100% - 140px)' }}>
                                <ChildOfferModalRightSide />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-between justify-content-md-end shadow-top overflow-auto">
                        <div className="d-flex flex-column-reverse flex-md-row justify-content-between w-100">
                            <Row className="mt-2 mt-md-0">
                                {canBeDeleted && !isClient && (
                                    <div className="col d-md-none">
                                        <Button
                                            variant="danger"
                                            className="btn-sm position-relative w-100"
                                            onClick={() => setShowDeleteModal(true)}
                                        >
                                            {t('general.button.delete')}
                                        </Button>
                                    </div>
                                )}
                                <div className="col">
                                    <Button
                                        variant="outline-primary"
                                        className="btn-sm position-relative ml-md-3 text-uppercase w-100"
                                        onClick={() => closeModal()}
                                    >
                                        {t('general.button.cancel')}
                                    </Button>
                                </div>
                            </Row>
                            <div className="d-flex align-items-center justify-content-between" style={{ gap: 8 }}>
                                <OfferPdfDownloadButton
                                    offerId={campaign?.offers[0]?.id}
                                    status={campaign?.offers[0]?.status}
                                    isZip={true}
                                />
                                {/*{(campaign?.offers[0]?.status === 'accepted' ||*/}
                                {/*    campaign?.offers[0]?.status === 'completed') && (*/}
                                {/*    <HtmlToPdf*/}
                                {/*        passedCampaignId={passedCampaignId ?? campaignId}*/}
                                {/*        passedOffers={campaign?.offers[0]?.id}*/}
                                {/*    />*/}
                                {/*)}*/}
                                <div className="d-md-flex mx-md-4 align-items-center mr-1">
                                    <div className="d-flex align-items-end">
                                        <h4 className="mb-md-0 text-uppercase mr-1 d-md-block d-none">
                                            {t('brand.campaign.book.offer.modal.grand_total')}
                                        </h4>
                                        <h6 className="mb-0 text-uppercase mr-1 d-md-none">
                                            {t('brand.campaign.book.offer.modal.grand_total')}
                                        </h6>
                                        {!isTransparentPricing && (
                                            <p className="mb-0 d-none d-md-block">
                                                {company?.package?.name === PackagesEnum.BASIC
                                                    ? t('brand.campaign.book.offer.modal.incl_booking_fee')
                                                    : t('brand.campaign.book.offer.modal.incl_reduced_fee')}
                                                :
                                            </p>
                                        )}
                                    </div>
                                    <h4 className="text-primary m-0 px-2 d-none d-md-block euro-symbol">
                                        {grandTotalForDisplaying}
                                    </h4>
                                    <h6 className="text-primary m-0 px-2 d-md-none euro-symbol">
                                        {grandTotalForDisplaying}
                                    </h6>
                                </div>
                                {isEditable && (
                                    <Button type="submit" className="btn-sm position-relative mr-2">
                                        {t('general.button.submit')}
                                    </Button>
                                )}
                                {canBeDeleted && !isClient && (
                                    <Button
                                        variant="danger"
                                        className="btn-sm position-relative mr-2 d-none d-md-block"
                                        onClick={() => setShowDeleteModal(true)}
                                    >
                                        {t('general.button.delete')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Modal.Footer>
                </form>
                <ConfirmSendingOfferModal
                    action={submitOfferRequest}
                    grandTotal={grandTotalForDisplaying}
                    show={showSubmitModal}
                    disableButton={disableButton}
                    closeModal={closeSubmitModal}
                    nbPosts={getNumberContentPieces(initialChildOffer.offerItems)}
                    user={selectedUser}
                    isChildOffer={true}
                    contractId={campaign?.contract?.id}
                />
                <ConfirmationModal
                    title={t('brand.campaign.book.offer.delete_offer')}
                    description={t('brand.campaign.book.offer.confirm_delete_offer')}
                    show={showDeleteModal}
                    closeModal={() => setShowDeleteModal(false)}
                    action={deleteOfferAction}
                />
            </Modal>
        </div>
    );
}

export default ChildOfferModal;
